/**
 * The front desk's own shell (Reception rebuild C1–C3). Who gets it is decided by
 * PERMISSION: reception:desk without admin:view (an admin holds the desk's
 * permission too, and keeps the admin's shell). The server's site_scope decides
 * what the desk reads; this only decides what is offered.
 */
import { hasPermFor } from '@/utils/permissions';
import type { UserRole } from '@/types';

type DeskUser = { role: UserRole; permissions?: string[] | null } | null | undefined;

export const RECEPTION_HOME = '/reception';

export function isReceptionShell(user: DeskUser): boolean {
  return !!user && hasPermFor(user, 'reception:desk') && !hasPermFor(user, 'admin:view');
}

/** A lobby desk (R6) holds no visitor:checkin; a company's desk does. */
export function isLobbyDesk(user: DeskUser): boolean {
  return isReceptionShell(user) && !hasPermFor(user, 'visitor:checkin');
}

export interface DeskNavItem { id: string; label: string; path: string; lobby?: boolean }
export interface DeskNavGroup { id: string; heading: string; items: DeskNavItem[] }

/** The approved groups. `lobby: true` is shown to a lobby desk too. */
export const RECEPTION_NAV_GROUPS: readonly DeskNavGroup[] = [
  { id: 'today', heading: 'Today', items: [
    { id: 'home', label: 'Front desk', path: '/reception', lobby: true },
    { id: 'alerts', label: 'Alerts', path: '/reception/alerts', lobby: true },
  ] },
  { id: 'visitors', heading: 'Visitors', items: [
    { id: 'checkin', label: 'Check in', path: '/reception/check-in', lobby: true },
    { id: 'visitors', label: 'Visitors', path: '/reception/visitors', lobby: true },
    { id: 'prereg', label: 'Pre-register', path: '/reception/pre-register' },
    { id: 'approvals', label: 'Approvals', path: '/reception/approvals' },
    { id: 'watchlist', label: 'Watchlist', path: '/reception/watchlist', lobby: true },
  ] },
  { id: 'registers', heading: 'Registers', items: [
    { id: 'mail', label: 'Mailroom', path: '/reception/mailroom', lobby: true },
    { id: 'passes', label: 'Gate passes', path: '/reception/gate-passes', lobby: true },
    { id: 'contractor-workers', label: 'Contractor workers', path: '/reception/contractor-workers', lobby: true },
    { id: 'tenant-cards', label: 'Tenant access cards', path: '/tenant-ops', lobby: true },
    { id: 'keys', label: 'Keys and cards', path: '/reception/keys-cards', lobby: true },
    { id: 'lost', label: 'Lost & found', path: '/reception/lost-found', lobby: true },
  ] },
  { id: 'bookings', heading: 'Bookings', items: [
    { id: 'rooms', label: 'Rooms', path: '/reception/rooms', lobby: true },
    { id: 'desks', label: 'Desks', path: '/spaces', lobby: true },
    { id: 'parking', label: 'Visitor parking', path: '/reception/parking', lobby: true },
    { id: 'catering', label: 'Catering orders', path: '/admin/catering', lobby: true },
  ] },
  { id: 'safety', heading: 'Safety', items: [
    { id: 'emergency', label: 'Emergency', path: '/reception/emergency', lobby: true },
    { id: 'rollcall', label: 'Roll call', path: '/reception/roll-call', lobby: true },
    { id: 'drills', label: 'Drill log', path: '/reception/drills', lobby: true },
    { id: 'concern', label: 'Private concern', path: '/reception/concern', lobby: true },
  ] },
  { id: 'reports', heading: 'Reports', items: [
    { id: 'daily', label: 'Daily report', path: '/reception/daily-report', lobby: true },
    { id: 'handover', label: 'Shift handover', path: '/reception/handover', lobby: true },
    { id: 'log', label: 'Visitor log', path: '/reception/visitor-log' },
  ] },
  { id: 'me', heading: 'Me', items: [
    { id: 'helpdesk', label: 'Helpdesk', path: '/my-requests', lobby: true },
    { id: 'profile', label: 'Profile', path: '/reception/profile', lobby: true },
  ] },
];

/** The groups as offered: what a lobby desk may use, and what the route map opens. */
export function receptionNavGroups(user: DeskUser, canOffer: (path: string) => boolean): DeskNavGroup[] {
  const lobby = isLobbyDesk(user);
  return RECEPTION_NAV_GROUPS
    .map((g) => ({ ...g, items: g.items.filter((i) => (!lobby || i.lobby) && canOffer(i.path)) }))
    .filter((g) => g.items.length > 0);
}

/** Phone bar: Home · Visitors · Scan · Registers · Menu (null opens the drawer). */
export const RECEPTION_BOTTOM_NAV: readonly { id: string; label: string; path: string | null }[] = [
  { id: 'home', label: 'Home', path: '/reception' },
  { id: 'visitors', label: 'Visitors', path: '/reception/visitors' },
  { id: 'scan', label: 'Scan', path: '/reception/check-in?tab=scan' },
  { id: 'registers', label: 'Registers', path: '/reception/registers' },
  { id: 'menu', label: 'Menu', path: null },
];
